import { useEffect, useRef } from "react";

// Import channel / streaming logos
import bbc from "../assets/cover/chaine/bbc.png";
import disco from "../assets/cover/chaine/disco.png";
import espn from "../assets/cover/chaine/espn.png";
import fg from "../assets/cover/chaine/fg.jpg";
import film from "../assets/cover/chaine/film.png";
import fubo from "../assets/cover/chaine/fubo.png";
import national from "../assets/cover/chaine/national.png";
import npo from "../assets/cover/chaine/npo.png";
import par from "../assets/cover/chaine/par.png";
import prime from "../assets/cover/chaine/prime.png";
import rtl from "../assets/cover/chaine/rtl.png";
import sbs from "../assets/cover/chaine/sbs.png";
import sdc from "../assets/cover/chaine/sdc.png";
import sky from "../assets/cover/chaine/sky.png";
import spi from "../assets/cover/chaine/spi.png";
import talpa from "../assets/cover/chaine/talpa.png";
import vrt from "../assets/cover/chaine/vrt.png";
import ziggo from "../assets/cover/chaine/ziggo1.png";

// Dynamically import all film images in assets/cover/film
const filmContext = require.context("../assets/cover/film", false, /\.(png|jpe?g|svg|webp)$/);
const filmImages = filmContext.keys().map((key) => filmContext(key));

export default function Chaine() {
  const carouselRef = useRef(null);
  const scrollInterval = useRef(null);

  const blockUserScroll = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const chaineLogos = [
    { name: "Ziggo Sport", src: ziggo },
    { name: "ESPN", src: espn },
    { name: "NPO Start", src: npo },
    { name: "VRT MAX", src: vrt },
    { name: "Prime Video", src: prime },
    { name: "BBC", src: bbc },
    { name: "Discovery+", src: disco },
    { name: "Fubo", src: fubo },
    { name: "National Geographic", src: national },
    { name: "Paramount+", src: par },
    { name: "RTL", src: rtl },
    { name: "SBS", src: sbs },
    { name: "Sky", src: sky },
    { name: "Talpa", src: talpa },
    { name: "Filmbox", src: film },
    { name: "FG", src: fg },
    { name: "SDC", src: sdc },
    { name: "SPI", src: spi },
  ];

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const startInfiniteScroll = () => {
      scrollInterval.current = setInterval(() => {
        const singleSetWidth = carousel.scrollWidth / 3;
        const currentScroll = carousel.scrollLeft;
        
        if (currentScroll >= singleSetWidth) {
          carousel.scrollLeft = 0;
        } else {
          carousel.scrollLeft += 1;
        }
      }, 20);
    };

    const initializeCarousel = () => {
      const images = carousel.querySelectorAll('img');
      const loadedImages = Array.from(images).filter(img => img.complete);
      
      if (loadedImages.length === images.length || loadedImages.length > 0) {
        startInfiniteScroll();
      } else {
        setTimeout(() => {
          startInfiniteScroll();
        }, 500);
      }
    };

    setTimeout(initializeCarousel, 100);

    return () => {
      if (scrollInterval.current) {
        clearInterval(scrollInterval.current);
      }
    };
  }, []);

  return (
    <>
      <section className="py-16 bg-slate-50">
        <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto">
          <div className="text-center mb-12">
            <h1 className="text-2xl md:text-3xl lg:text-4xl mx-auto font-black bg-clip-text bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] mb-4 max-w-3xl leading-snug text-center">
              Onbeperkt streamen van de nieuwste films, series en live TV-kanalen
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Geniet van premium content van de beste streamingplatforms in één abonnement
            </p>
          </div>

          {/* Infinite Carousel */}
          <div className="relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-transparent z-10"></div>
            <div
              ref={carouselRef}
              className="flex space-x-4 overflow-hidden carousel-container"
              style={{
                scrollBehavior: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                touchAction: "none",
                overscrollBehaviorX: "contain",
              }}
              onWheel={blockUserScroll}
              onTouchStart={blockUserScroll}
              onTouchMove={blockUserScroll}
              onPointerDown={blockUserScroll}
              onMouseDown={blockUserScroll}
            >
              {[...filmImages, ...filmImages, ...filmImages].map(
                (image, index) => {
                  return (
                    <div
                      key={`${index}-${Math.random()}`}
                      className="flex-shrink-0 group relative"
                    >
                      <div className="relative w-48 h-80 md:w-52 md:h-[22rem] rounded-2xl overflow-hidden transition-all duration-500 shadow-md mb-2">
                        <img
                          src={image}
                          alt={`Film ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-500 carousel-image"
                          draggable={false}
                          onDragStart={(e) => e.preventDefault()}
                        />
                      </div>
                    </div>
                  );
                }
              )}
            </div>
            {/* Edge fade overlays */}
            <div className="carousel-edge-left"></div>
            <div className="carousel-edge-right"></div>
          </div>

          {/* Streaming Services Grid */}
          <div className="mt-5">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {chaineLogos.map((logo, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-110 border border-gray-100"
                >
                  <div className="flex items-center justify-center h-8">
                    <img
                      src={logo.src}
                      alt={logo.name}
                      className="max-h-12 max-w-full object-contain filter group-hover:brightness-110 transition-all duration-300"
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
